// Muss der ERSTE Import bleiben: rüstet axios.create aus, bevor irgendein Modul eine Instanz anlegt (Wartungsmodus).
import './utils/wartungStart';
import React, { Suspense, lazy } from 'react';
import VerteilerPage from './pages/VerteilerPage';
import VerteilerSignupPage, { VerteilerConfirmPage } from './pages/VerteilerSignupPage';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { installGlobalErrorReporting } from './utils/errorReport';
import { installPromptMitschneiden } from './utils/pwaInstall';
// Klein und ohne App-Abhängigkeiten (nur die reine Seitenliste) — deshalb nicht verzögert geladen
import AlteAdresse from './navigation/AlteAdresse';
import { ladeRundung } from './utils/rundung';
import { starteFensterFokus } from './components/ui/fensterFokus';
import NeueVersionKarte from './components/NeueVersionKarte';

// Globale Client-Fehler ins In-App-Fehler-Log melden (best effort, gedrosselt).
installGlobalErrorReporting();

// #309: `beforeinstallprompt` feuert frueh — oft bevor React steht. Wird es hier nicht
// mitgeschnitten, ist es beim Rendern des Hinweises laengst verpufft.
installPromptMitschneiden();

// #523: Rundungen aus dem Branding — vor dem ersten Rendern, damit nichts springt.
ladeRundung();

// Code-Split: Admin-App als separater Webpack-Chunk
const App = lazy(() => import('./App'));

// Code-Split: Helfer-Portal als separater Webpack-Chunk
const HelferApp = lazy(() => import('./HelferApp'));
// Materiallager (E5, #695): Lager-Tablet ohne Benutzerkonto, eigener Chunk
const LagerTabletApp = lazy(() => import('./lagerTablet/LagerTabletApp'));

// Code-Split: Öffentliche Routen als separate Webpack-Chunks
const PublicView = lazy(() => import('./components/PublicView'));
const StructurePublicView = lazy(() => import('./components/StructurePublicView'));
// #659: LES-Ansicht (ersetzt die frühere Master-Seite /master/<token>; die Adresse gibt es nicht mehr)
const LesAnsicht = lazy(() => import('./components/les/LesAnsicht'));
const SurveyPublicView = lazy(() => import('./components/SurveyPublicView'));
const WithdrawalPage = lazy(() => import('./components/WithdrawalPage'));
const LegalPage = lazy(() => import('./components/LegalPage'));
const AvailabilityOverview = lazy(() => import('./components/AvailabilityOverview'));
// #640 Vorsortieren (Etappe c): das Fenster als eigene Seite (zweiter Bildschirm); nur für angemeldete Planende mit Modul, die Seite prüft das selbst
const VorsortierenSeite = lazy(() => import('./planung/vorsortieren/VorsortierenSeite'));
const EinteilenSeite = lazy(() => import('./planung/einteilen/EinteilenSeite'));
// #641 Übersicht für den Vorschlag (Z4): die Tabelle als eigene Seite (zweiter Bildschirm); nur für angemeldete Planende mit Modul, die Seite prüft das selbst
const UebersichtSeite = lazy(() => import('./planung/uebersicht/UebersichtSeite'));
const OrgMeldelink = lazy(() => import('./components/OrgMeldelink'));
const OrgAccountInvite = lazy(() => import('./components/OrgAccountInvite'));
// E4: Einladung zu einem Konto annehmen (ohne Anmeldung, mit Rechtslinks)
const EinladungAnnehmen = lazy(() => import('./pages/EinladungAnnehmen'));
const FeedbackPage = lazy(() => import('./pages/FeedbackPage'));
const InquiryPage = lazy(() => import('./components/InquiryPage'));
// #482: Seite der Einsatzleitung für die Dienstmappe (ohne Anmeldung)
const DienstmappePage = lazy(() => import('./pages/DienstmappePage'));
// #508 Infoseite: eigener Chunk — öffentlich, ohne die Verwaltungs-App
const InfoSeitePage = lazy(() => import('./infoseite/InfoSeitePage'));
const InfoSeiteEditorPage = lazy(() => import('./infoseite/editor/InfoSeiteEditorPage'));
// AdminPasswordReset hat nur Named Exports (kein default export)
const AdminPasswordResetRequest = lazy(() =>
  import('./components/AdminPasswordReset').then(m => ({ default: m.AdminPasswordResetRequest }))
);
const AdminPasswordResetComplete = lazy(() =>
  import('./components/AdminPasswordReset').then(m => ({ default: m.AdminPasswordResetComplete }))
);

const HelferLoading = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#f8fafc', fontFamily: "'Inter', sans-serif",
  }}>
    <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Laden...</p>
  </div>
);

// UI/UX-Audit (#556): Tastatur-Führung für alle Fenster (data-fenster) — einmal für Verwaltung, Portal und öffentliche Seiten
starteFensterFokus();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Suspense fallback={<HelferLoading />}>
      <Routes>
        {/* Redirect /helfer/login to combined login */}
        <Route path="/helfer/login" element={<Navigate to="/login" replace />} />
        {/* Helfer-Portal (separater Chunk) */}
        <Route path="/helfer/*" element={<HelferApp />} />
        <Route path="/lager-tablet/*" element={<LagerTabletApp />} />
        <Route path="/public/:token" element={<PublicViewWrapper />} />
        <Route path="/les" element={<LesAnsicht />} />
        <Route path="/structure/:token" element={<StructurePublicViewWrapper />} />
        <Route path="/survey/:token" element={<SurveyPublicViewWrapper />} />
        <Route path="/sanlog/:token" element={<SanlogViewWrapper />} />
        <Route path="/passwort-vergessen" element={<AdminPasswordResetRequest />} />
        <Route path="/passwort-reset/:token" element={<AdminPasswordResetComplete />} />
        <Route path="/einladung/:token" element={<EinladungAnnehmen />} />
        <Route path="/abmeldung" element={<WithdrawalPage />} />
        <Route path="/dienstmappe/:token" element={<DienstmappePage />} />
        <Route path="/info/:name" element={<InfoSeitePage />} />
        {/* #380: Verteiler waehlen und verlassen — eine Seite, ohne Anmeldung, denn viele
            Adressen gehoeren Menschen ohne Portal-Konto. `/abbestellen` bleibt gueltig,
            damit Links in bereits verschickten Mails weiter funktionieren. */}
        <Route path="/abbestellen/:token" element={<VerteilerPage />} />
        <Route path="/verteiler/:token" element={<VerteilerPage />} />
        <Route path="/verteiler" element={<VerteilerSignupPage />} />
        <Route path="/verteiler/bestaetigen/:token" element={<VerteilerConfirmPage />} />
        {/* #373: Einladung einloesen — MUSS vor der allgemeinen Meldelink-Route stehen. */}
        <Route path="/org-melde/:token/zugang/:inviteToken" element={<OrgAccountInvite />} />
        <Route path="/org-melde/:token" element={<OrgMeldelink />} />
        <Route path="/inquiry/:token" element={<InquiryPageWrapper />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/impressum" element={<LegalPage type="impressum" />} />
        <Route path="/datenschutz" element={<LegalPage type="datenschutz" />} />
        <Route path="/events/:eventId/availability" element={<AvailabilityOverview />} />
        <Route path="/events/:eventId/vorsortieren" element={<VorsortierenSeite />} />
        <Route path="/events/:eventId/einteilen" element={<EinteilenSeite />} />
        <Route path="/events/:eventId/uebersicht" element={<UebersichtSeite />} />
        <Route path="/events/:eventId/infoseite" element={<InfoSeiteEditorPage />} />
        <Route path="/infoseiten-vorlagen/:vorlageId" element={<InfoSeiteEditorPage />} />
        {/* V6.9.0 Infoseiten-Verwaltung: Editor über die Seiten-Kennung (auch ohne Ereignis) */}
        <Route path="/infoseiten/:seiteId" element={<InfoSeiteEditorPage />} />
        <Route path="/events/:eventId/abfragelink" element={<App />} />
        <Route path="/events/:eventId" element={<App />} />
        <Route path="/events" element={<App />} />
        <Route path="/verwaltung/*" element={<App />} />
        <Route path="/verwaltung" element={<App />} />
        {/* #483 2b: Personen — alte Adressen leiten dorthin um */}
        <Route path="/personen/*" element={<App />} />
        <Route path="/personen" element={<App />} />
        <Route path="/email-verteiler" element={<AlteAdresse />} />
        <Route path="/anmeldungen" element={<App />} />
        {/* #483 2c: „Anmeldungen“ statt „Abfragelinks“ — alte Lesezeichen laufen weiter */}
        <Route path="/abfragelinks" element={<Navigate to="/anmeldungen" replace />} />
        {/* Die Statistik liegt seit V5.369.0 unter Verwaltung. Alte Lesezeichen und Links
            aus Mails landen dort, statt ins Leere zu laufen. */}
        <Route path="/statistik" element={<Navigate to="/verwaltung/statistik" replace />} />
        {/* Monatsabschluss (#313) — seit V5.369.0 eigene Ansicht im Kopf statt Unterreiter
            in der Verwaltung. Ohne diese Zeile faengt der Catch-all sie ab. */}
        <Route path="/abrechnung" element={<App />} />
        {/* Materiallager (Modul `lager`) */}
        <Route path="/lager" element={<App />} />
        <Route path="/planungsrechner" element={<App />} />
        <Route path="/kalkulationen/:kalkulationId" element={<App />} />
        <Route path="/kalkulationen" element={<App />} />
        <Route path="/infoseiten" element={<App />} />
        <Route path="/tools" element={<App />} />
        <Route path="/benutzer" element={<AlteAdresse />} />
        <Route path="/login" element={<App />} />
        {/* Redirects from old URLs */}
        <Route path="/users" element={<AlteAdresse />} />
        <Route path="/templates" element={<Navigate to="/verwaltung/vorlagen" replace />} />
        <Route path="/categories" element={<Navigate to="/verwaltung/kategorien" replace />} />
        <Route path="/" element={<Navigate to="/events" replace />} />
        <Route path="*" element={<Navigate to="/events" replace />} />
      </Routes>
      </Suspense>
      {/* Mockup app-aktualisieren.html (1D): neue Version anbieten, wenn nicht still neu geladen werden darf */}
      <NeueVersionKarte />
    </BrowserRouter>
  </React.StrictMode>
);

function PublicViewWrapper() {
  const token = window.location.pathname.match(/^\/public\/([a-f0-9]+)$/)?.[1] || '';
  return <PublicView token={token} />;
}

function StructurePublicViewWrapper() {
  const token = window.location.pathname.match(/^\/structure\/([a-f0-9]+)$/)?.[1] || '';
  return <StructurePublicView token={token} />;
}

function SurveyPublicViewWrapper() {
  const token = window.location.pathname.match(/^\/survey\/([a-f0-9]+)$/)?.[1] || '';
  return <SurveyPublicView token={token} />;
}

function SanlogViewWrapper() {
  const token = window.location.pathname.match(/^\/sanlog\/([a-f0-9]+)$/)?.[1] || '';
  return <PublicView token={token} sanlogMode={true} />;
}

const inquiryLightTheme = {
  bg: '#e2e8f0', bgCard: '#ffffff', bgInput: '#f1f5f9', border: '#94a3b8',
  text: '#0f172a', textSec: '#1e293b', textMuted: '#475569',
  accent: '#2563eb',
};

function InquiryPageWrapper() {
  const token = window.location.pathname.match(/^\/inquiry\/([^/]+)/)?.[1] ?? '';
  return <InquiryPage token={token} theme={inquiryLightTheme} />;
}

// Register service worker for offline support (PWA) — nicht im iframe (#608, LES2 bettet /public/<token> ein):
// dort gibt es keine PWA, und ein Worker würde je einbettender Seite eigene Caches anlegen und neu laden.
if (window.self === window.top) serviceWorkerRegistration.register({
  onUpdate: () => {
    // Das Neuladen uebernimmt `controllerchange` in serviceWorkerRegistration — es feuert
    // erst, wenn der neue Worker die Steuerung wirklich hat. Ein sofortiges reload() hier
    // wuerde noch vom ALTEN Worker bedient und zeigte weiter den alten Stand.
    console.log('[SW] Neue Fassung wird übernommen.');
  },
});
